import React from 'react';
import _ from 'underscore';

import {canRequestAccess, requestAccess} from 'auth/RequestAccess';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import notifications from 'notifications';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';
import {ViewStateParams, WorkflowSelector} from 'workflows/reduceWorkflows';
import {StageRunGraph} from 'workflows/visualization/TaggedNodeGraph';

import {getWorkflowDisplayString, isWorkflowInitialized} from './util/WorkflowUtil';
import * as WorkflowActions from './WorkflowActions';
import WorkflowConfigureScreen from './WorkflowConfigureScreen';
import {
  ServerStageEntry,
  StageConfiguration,
  StageEntryDocument,
  StageEntrySummaryData,
  StageFlowNode,
  StageRun,
  StageRunSample,
  Workflow,
  WorkflowScreen,
  WorkflowStage,
  WorkflowTemplate,
  WorkflowTemplateVersion,
} from './WorkflowConstants';
import WorkflowDashboardContainer from './WorkflowDashboardContainer';
import WorkflowDetailsScreen from './WorkflowDetailsScreen';
import * as WorkflowTemplateActions from './WorkflowTemplateActions';
import WorkflowTemplateViewScreen from './WorkflowTemplateViewScreen';

interface WorkflowDashboardScreenProps {}

interface WorkflowDashboardTemplatesScreenProps {
  workflowTemplates: Array<WorkflowTemplate> | null;
}

interface WorkflowDetailsScreenProps {
  workflowTemplateVersion: WorkflowTemplateVersion | null | undefined;
  stageConfigurations: Array<StageConfiguration> | null | undefined;
  stageConfigurationIdToStageFlowNodes: {[id: string]: Array<StageFlowNode>} | null;
  stageIdToStageEntries: {[stageId: string]: Array<ServerStageEntry>};
  stageIdToStageRuns: {[stageId: string]: Array<StageRun>};
  stageIdToStageConfiguration: {[stageId: string]: StageConfiguration};
  stageIdToStructuredData: {[stageId: string]: StructuredData};
  stageRunSampleById: {[id: string]: StageRunSample};
  workflow: Workflow | undefined;
  workflowStages: Array<WorkflowStage> | undefined;
  entryIdToSummaryData: {[entryId: string]: StageEntrySummaryData};
}

interface WorkflowTemplateViewScreenProps {
  workflowTemplate: WorkflowTemplate | undefined;
  workflowTemplateVersion: WorkflowTemplateVersion | null | undefined;
  stageConfigurations: Array<StageConfiguration> | null | undefined;
  stageConfigurationIdToStageFlowNodes: {[id: string]: Array<StageFlowNode>};
}

export type ScreenProps<Screen extends WorkflowScreen> = Screen extends WorkflowScreen.WORKFLOW_DASHBOARD
  ? WorkflowDashboardScreenProps
  : Screen extends WorkflowScreen.WORKFLOW_DASHBOARD_TEMPLATES
    ? WorkflowDashboardTemplatesScreenProps
    : Screen extends WorkflowScreen.WORKFLOW_DETAILS
      ? WorkflowDetailsScreenProps
      : Screen extends WorkflowScreen.WORKFLOW_TEMPLATE_VIEW
        ? WorkflowTemplateViewScreenProps
        : never;

export interface Info<Screen extends WorkflowScreen> {
  fetch: (params: ViewStateParams<Screen>) => void;
  readFromStore: (workflowSelector: WorkflowSelector, params: ViewStateParams<Screen>) => ScreenProps<Screen>;
  title: (props: ScreenProps<Screen>) => string;
  getUrlParam: (params: ViewStateParams<Screen>) => string | null;
  render: (props: ScreenProps<Screen> & {isExpanded: boolean}) => JSX.Element;
}

// Each object in ScreenInfo represents a different screen that this component can render.
// These objects have three methods:
//    * fetch(params) -- fetch the data model from the server when the screen is first rendered
//    * readFromStore(params) -- read the data model from the store; this object will be merged with the
//                               viewState object from the store to create the final state
//    * render(state) -- render the object given the state (viewState + result of readFromStore)
//    * getUrlParam(params) -- returns the parameter value that is used as part of the url, or null
//      if the URL doesn't have any arguments
const ScreenInfo: {[Screen in WorkflowScreen]: Info<Screen>} = {
  [WorkflowScreen.WORKFLOW_DASHBOARD]: {
    fetch(): void {},
    readFromStore(): {} {
      return {};
    },
    title(): string {
      return getWorkflowDisplayString('Legacy Workflows', 'Workflows');
    },
    getUrlParam(): null {
      return null;
    },
    render(props: {isExpanded: boolean}): JSX.Element {
      return <WorkflowDashboardContainer {...props} screen={WorkflowScreen.WORKFLOW_DASHBOARD} />;
    },
  },
  [WorkflowScreen.WORKFLOW_DASHBOARD_TEMPLATES]: {
    fetch(): void {
      WorkflowTemplateActions.fetchAllWorkflowTemplates();
    },
    readFromStore(workflowSelector: WorkflowSelector): {workflowTemplates: Array<WorkflowTemplate> | null} {
      return {workflowTemplates: workflowSelector.getAllWorkflowTemplates()};
    },
    title(): string {
      return getWorkflowDisplayString('Legacy Workflow Templates', 'Workflow Templates');
    },
    getUrlParam(): null {
      return null;
    },
    render({workflowTemplates}: {workflowTemplates: Array<WorkflowTemplate> | null}): JSX.Element {
      return (
        <WorkflowDashboardContainer
          workflowTemplates={workflowTemplates}
          screen={WorkflowScreen.WORKFLOW_DASHBOARD_TEMPLATES}
        />
      );
    },
  },
  [WorkflowScreen.WORKFLOW_DETAILS]: {
    async fetch({workflowId}: {workflowId: string}): Promise<void> {
      try {
        await WorkflowActions.fetchWorkflowDetails(workflowId);
      } catch (e) {
        if (!net.isServerRequestError(e)) {
          throw e;
        }
        if (canRequestAccess(e.responseJSON)) {
          requestAccess(e.responseJSON);
          WorkflowActions.openWorkflowDashboardScreen();
        } else {
          notifications.notifyError(e);
        }
      }
    },
    readFromStore(
      workflowSelector: WorkflowSelector,
      {workflowId}: {workflowId: string}
    ): WorkflowDetailsScreenProps {
      const workflow = workflowSelector.getWorkflow(workflowId);
      return {
        workflowTemplateVersion: workflow
          ? workflowSelector.getWorkflowTemplateVersion(workflow.workflowTemplateVersion.id)
          : null,
        stageConfigurations: workflow
          ? workflowSelector.getStageConfigurations(workflow.workflowTemplateVersion.id)
          : null,
        stageConfigurationIdToStageFlowNodes: workflow
          ? workflowSelector.getStageConfigurationIdToStageFlowNodes(workflow.workflowTemplateVersion.id)
          : null,
        stageIdToStageEntries: workflowSelector.getStageIdToStageEntries(workflowId),
        stageIdToStageRuns: workflowSelector.getStageIdToStageRuns(workflowId),
        stageIdToStageConfiguration: workflowSelector.getStageIdToStageConfiguration(workflowId),
        stageIdToStructuredData: workflowSelector.getStageIdToStructuredData(workflowId),
        stageRunSampleById: workflowSelector.getStageRunSampleById(),
        workflow,
        workflowStages: workflowSelector.getWorkflowStages(workflowId),
        entryIdToSummaryData: workflowSelector.getEntryIdToSummaryData(),
      };
    },
    title({workflow}: WorkflowDetailsScreenProps): string {
      return workflow ? workflow.humanId : getWorkflowDisplayString('Legacy Workflow', 'Workflow');
    },
    getUrlParam(params: {workflowId: string}): string {
      return params.workflowId;
    },
    render({
      workflowTemplateVersion,
      stageConfigurations,
      stageConfigurationIdToStageFlowNodes,
      workflow,
      workflowStages,
      stageIdToStageRuns,
      stageIdToStructuredData,
      isExpanded,
      stageIdToStageConfiguration,
      stageRunSampleById,
      stageIdToStageEntries,
      entryIdToSummaryData,
    }: {
      workflowTemplateVersion: WorkflowTemplateVersion;
      stageConfigurations: Array<StageConfiguration>;
      stageConfigurationIdToStageFlowNodes: {[id: string]: Array<StageFlowNode>};
      workflow: Workflow;
      workflowStages: Array<WorkflowStage>;
      stageIdToStageRuns: {[stageId: string]: Array<StageRun>};
      stageIdToStructuredData: {[stageId: string]: StructuredData};
      isExpanded: boolean;
      stageIdToStageConfiguration: {[stageId: string]: StageConfiguration};
      stageRunSampleById: {[id: string]: StageRunSample};
      stageIdToStageEntries: {[stageId: string]: Array<StageEntryDocument>};
      entryIdToSummaryData: {[entryId: string]: StageEntrySummaryData};
    }): JSX.Element {
      if (!(workflow && workflowStages)) {
        return (
          <div className="text-center u-padding-top">
            <HorizontalThrobber />
          </div>
        );
      } else if (isWorkflowInitialized(workflow)) {
        // eslint-disable-next-line no-restricted-syntax
        const allStageRuns = _.flatten(Object.values(stageIdToStageRuns));
        const stageRunGraph = new StageRunGraph(allStageRuns, stageConfigurations);
        return (
          <WorkflowDetailsScreen
            isExpanded={isExpanded}
            stageIdToStageEntries={stageIdToStageEntries}
            stageIdToStageRuns={stageIdToStageRuns}
            stageIdToStageConfiguration={stageIdToStageConfiguration}
            stageIdToStructuredData={stageIdToStructuredData}
            stageRunGraph={stageRunGraph}
            stageRunSampleById={stageRunSampleById}
            workflow={workflow}
            workflowStages={workflowStages}
            entryIdToSummaryData={entryIdToSummaryData}
          />
        );
      } else {
        return (
          <WorkflowConfigureScreen
            workflow={workflow}
            workflowTemplateVersion={workflowTemplateVersion}
            stageConfigurations={stageConfigurations}
            stageConfigurationIdToStageFlowNodes={stageConfigurationIdToStageFlowNodes}
          />
        );
      }
    },
  },
  [WorkflowScreen.WORKFLOW_TEMPLATE_VIEW]: {
    fetch({workflowTemplateId}: {workflowTemplateId: string}): void {
      WorkflowTemplateActions.fetchWorkflowTemplateAndLatestVersion(workflowTemplateId);
    },
    readFromStore(
      workflowSelector: WorkflowSelector,
      {
        workflowTemplateId,
        selectedWorkflowTemplateVersionId,
      }: {workflowTemplateId: string; selectedWorkflowTemplateVersionId: string}
    ): WorkflowTemplateViewScreenProps {
      return {
        workflowTemplate: workflowSelector.getWorkflowTemplate(workflowTemplateId),
        workflowTemplateVersion: selectedWorkflowTemplateVersionId
          ? workflowSelector.getWorkflowTemplateVersion(selectedWorkflowTemplateVersionId)
          : null,
        stageConfigurations: selectedWorkflowTemplateVersionId
          ? workflowSelector.getStageConfigurations(selectedWorkflowTemplateVersionId)
          : null,
        stageConfigurationIdToStageFlowNodes: workflowSelector.getStageConfigurationIdToStageFlowNodes(
          selectedWorkflowTemplateVersionId
        ),
      };
    },
    title({workflowTemplate}: WorkflowTemplateViewScreenProps): string {
      return workflowTemplate ? workflowTemplate.name : 'Workflow Template';
    },
    getUrlParam(params: {workflowTemplateId: string}): string {
      return params.workflowTemplateId;
    },
    render(props: WorkflowTemplateViewScreenProps & {isExpanded: boolean}): JSX.Element {
      const {
        workflowTemplate,
        workflowTemplateVersion,
        stageConfigurations,
        stageConfigurationIdToStageFlowNodes,
      } = props;
      if (!(workflowTemplate && workflowTemplateVersion && stageConfigurations)) {
        return (
          <div className="text-center u-padding-top">
            <HorizontalThrobber />
          </div>
        );
      }
      return (
        <WorkflowTemplateViewScreen
          workflowTemplate={workflowTemplate}
          workflowTemplateVersion={workflowTemplateVersion}
          stageConfigurations={stageConfigurations}
          stageConfigurationIdToStageFlowNodes={stageConfigurationIdToStageFlowNodes}
        />
      );
    },
  },
};

export default ScreenInfo;
